import Button from '@/components/Buttons/Button';
import { toast } from '@/components/toast';
import { SubmitFooterType, TransparentButton } from '@/components/Voice/VoiceModal/SelectVoiceContent/SelectVoiceContent';
import { VoiceModal } from '@/components/Voice/VoiceModal/VoiceModal';
import { VoiceFiltersEnum } from '@/hooks/useFilteredVoices';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    CacheStatus,
    PolotnoStoreActivePageKeys,
    getPageProperty,
    usePolotnoActivePageState,
    useSetPolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { getVoiceFromCurrentSentiment } from '@/pages/AdvancedEditor/utils/expressive-utils';
import { getValidSpeed } from '@/pages/AdvancedEditor/utils/speedLimits';
import { ttsVoiceLanguageFilter } from '@/state/presenters/voice-filters';
import { ttsVoiceModal } from '@/state/presenters/voice-modals';
import { IVoice, Providers, ScriptType } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useRecoilState, useRecoilValue } from 'recoil';

const Footer = styled.div`
    display: flex;
    padding: 16px 36px;
    justify-content: end;
    gap: 16px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
`;

const StyledTransparentButton = styled(TransparentButton)`
    padding: 6px 16px;
    height: 4rem;
`;

enum SceneSwitchOptions {
    THIS_AVATAR,
    ALL_AVATARS,
}

export const AdvancedEditorVoiceModal = observer(() => {
    const { store } = useAdvancedEditorContext();
    const [selectedVoice, setSelectedVoice] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedVoice);
    const setTtsIsSelectedDefault = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsIsSelectedDefault);
    const setTtsSelectedLanguage = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedLanguage);
    const setTtsAudioCacheStatus = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
    const setSceneDurationCalculated = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.sceneDurationCalculated);
    const setTtsCreated = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsCreated);
    const setTtsError = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.ttsError);
    const [selectedScript] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedScript);
    const [selectedSentimentId] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedSentimentId);
    const [voiceModalState, setVoiceModalState] = useRecoilState(ttsVoiceModal);
    const languageRecoil = useRecoilValue(ttsVoiceLanguageFilter);
    const avatarPresenter = getAvatarPresenterFromPage(store.activePage);

    const currentSentimentVoice = getVoiceFromCurrentSentiment(selectedSentimentId, avatarPresenter);
    const injectedVoices = currentSentimentVoice ? [currentSentimentVoice] : [];

    function checkAndWarnSentimentSupport(previousVoice: IVoice | null | undefined, newVoice: IVoice) {
        const wasPreviousVoiceInjected = previousVoice && injectedVoices?.some(v => v.id === previousVoice.id);
        const isNewVoiceInjected = injectedVoices?.some(v => v.id === newVoice.id);

        if (wasPreviousVoiceInjected && !isNewVoiceInjected) {
            toast.info({
                title: 'The voice you selected does not support sentiments',
                toastId: 'voice-sentiment-warning',
            });
        }
    }

    function handleSelectedVoiceChange(voice: IVoice) {
        checkAndWarnSentimentSupport(selectedVoice, voice);

        setTtsSelectedLanguage(voice.language);
        setSelectedVoice(voice);
        setTtsIsSelectedDefault(false);

        if (selectedScript === ScriptType.text) {
            setTtsAudioCacheStatus(CacheStatus.Invalid);
            setSceneDurationCalculated(false);
            setTtsCreated(false);
        }
    }

    function handleAvatarsApplyVoice(modalSelectedVoice: IVoice, selectionMode: SceneSwitchOptions) {
        checkAndWarnSentimentSupport(selectedVoice, modalSelectedVoice);

        if (selectionMode === SceneSwitchOptions.THIS_AVATAR) {
            setTtsSelectedLanguage(modalSelectedVoice.language);
            setSelectedVoice(modalSelectedVoice);
            setTtsIsSelectedDefault(false);
            setTtsError(null);

            if (selectedScript === ScriptType.text) {
                setTtsAudioCacheStatus(CacheStatus.Invalid);
                setSceneDurationCalculated(false);
                setTtsCreated(false);
            }
        } else {
            store.pages.forEach(page => {
                const pageVoiceConfig = getPageProperty(page, PolotnoStoreActivePageKeys.ttsVoiceConfig);
                const validVoiceConfig = getValidSpeed(pageVoiceConfig, modalSelectedVoice.provider);

                page.set({
                    custom: {
                        ...page.custom,
                        [PolotnoStoreActivePageKeys.ttsSelectedLanguage]: modalSelectedVoice.language,
                        [PolotnoStoreActivePageKeys.ttsSelectedVoice]: modalSelectedVoice,
                        [PolotnoStoreActivePageKeys.ttsVoiceConfig]: validVoiceConfig,
                        [PolotnoStoreActivePageKeys.ttsError]: false,
                        ...(getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text && {
                            [PolotnoStoreActivePageKeys.sceneDurationCalculated]: false,
                            [PolotnoStoreActivePageKeys.ttsCreated]: false,
                        }),
                    },
                    temporary: {
                        // @ts-ignore
                        ...page.temporary,
                        [PolotnoStoreActivePageKeys.ttsIsSelectedDefault]: false,
                        ...(getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text && {
                            [PolotnoStoreActivePageKeys.ttsAudioCacheStatus]: CacheStatus.Invalid,
                        }),
                    },
                });
            });
        }
    }

    const SubmitFooter: SubmitFooterType = ({ onSubmitted, selectedVoice }: { onSubmitted: () => void; selectedVoice: IVoice | null }) => (
        <Footer>
            <StyledTransparentButton
                data-testid="video-editor-voice-modal-apply-all-avatars-voice-button"
                onClick={() => {
                    if (selectedVoice) {
                        handleAvatarsApplyVoice(selectedVoice, SceneSwitchOptions.ALL_AVATARS);
                    }
                    onSubmitted();
                }}>
                Apply to all Avatars
            </StyledTransparentButton>

            <Button
                data-testid="video-editor-voice-modal-apply-this-avatar-voice-button"
                onClick={() => {
                    if (selectedVoice) {
                        handleAvatarsApplyVoice(selectedVoice, SceneSwitchOptions.THIS_AVATAR);
                    }
                    onSubmitted();
                }}
                variant="contained"
                color="secondary">
                Apply voice
            </Button>
        </Footer>
    );

    const voicesCustomFilter = (voice: IVoice) => {
        if (voice.provider === Providers.AzureOpenAi) {
            return voice.name.includes(' HD ') || voice.name.endsWith(' HD');
        }
        return true;
    };

    return (
        <VoiceModal
            open={voiceModalState}
            onClose={() => setVoiceModalState(false)}
            SubmitFooter={SubmitFooter}
            selectedVoice={selectedVoice}
            onSelectedVoiceChange={handleSelectedVoiceChange}
            filters={{
                [VoiceFiltersEnum.LANGUAGE]: languageRecoil === 'all' ? undefined : languageRecoil,
                [VoiceFiltersEnum.CUSTOM]: voicesCustomFilter,
            }}
            injectedVoices={injectedVoices}
        />
    );
});
